/**
 * Ein Tag im Monat (Board „Monat je Apartment“): Datum, heute als Pille, Preis mit Schloss und
 * Mindestnächte; vergangene Tage grau und nicht wählbar, gebuchte ohne Preis (der Balken liegt
 * darüber), Sperre, Verkaufsstopp, Verbund und Kanalregel mit Wort oder Zeichen.
 */
import type { KeyboardEvent, PointerEvent } from 'react';
import { DoubleBookedMark } from '@/features/properties/flat-family/DoubleBookedMark';
import { cn } from '@/shared/lib/cn';
import { CloseoutMark, cellTitle, stateLabel } from '../calendar/DayCellBody';
import type { DayCellModel } from '../calendar/calendarModel';
import { parseIso, isWeekendIso } from '../calendar/calendarDates';
import type { SelectionMode } from '../calendar/rangeSelectionModel';
import { PublicationMark, automationText } from '../automation/AutomationMarks';
import type { AutomationCell } from '../automation/automationModel';
import { MonthCellValues } from './MonthCellValues';
import { MONTH_SHORT } from './monthModel';

interface Props {
  cell: DayCellModel;
  currency: string;
  today: string;
  first: boolean;
  selected: boolean;
  edge: boolean;
  automation?: AutomationCell | null;
  /** Handy: 64 px hoch, nur Datum und Preis. */
  compact?: boolean;
  onBegin?: (date: string, mode: SelectionMode, immediate?: boolean) => void;
  onExtend?: (date: string) => void;
}

export function MonthCell({
  cell,
  currency,
  today,
  first,
  selected,
  edge,
  automation = null,
  compact = false,
  onBegin,
  onExtend,
}: Props) {
  const { day } = cell;
  const date = parseIso(day.date);
  const past = day.date < today;
  const marked = first || date.getUTCDate() === 1;
  const label = !marked
    ? String(date.getUTCDate())
    : compact
      ? `${date.getUTCDate()}.${date.getUTCMonth() + 1}.`
      : `${date.getUTCDate()}. ${MONTH_SHORT[date.getUTCMonth()]}`;
  const booked = cell.kind === 'booked' || cell.kind === 'doubleBooked';
  const interactive = Boolean(onBegin) && !past;
  const className = cn(
    'border-hairline relative flex min-w-0 select-none flex-col gap-1 border-l text-left',
    compact ? 'h-16 items-center px-1 py-1.5' : 'h-28 items-start px-3 py-2.5',
    past ? 'bg-paper/70' : selected ? 'bg-forest-soft' : isWeekendIso(day.date) && 'bg-paper/40',
    selected && edge && 'shadow-[inset_0_0_0_2px_var(--color-forest)]',
    interactive && !selected && 'hover:bg-forest-soft/60 cursor-pointer',
  );
  const body = (
    <>
      <span
        className={cn(
          'font-mono font-semibold',
          compact ? 'text-[11px]' : 'text-xs',
          day.date === today
            ? cn(
                'rounded-pill bg-forest flex items-center text-white',
                compact ? 'h-5 px-1.5' : 'h-6 px-2',
              )
            : past
              ? 'text-ink-faint'
              : 'text-ink',
        )}
      >
        {label}
      </span>
      {!past && !booked && (
        <MonthCellValues
          cell={cell}
          currency={currency}
          automation={automation}
          compact={compact}
        />
      )}
      {!past && <PublicationMark cell={automation} />}
      {cell.closeout && !past && !booked && <CloseoutMark closeout={cell.closeout} />}
      {cell.kind === 'doubleBooked' && (
        <DoubleBookedMark className="absolute inset-x-3 bottom-1.5" />
      )}
    </>
  );
  const aria = `${day.date}: ${stateLabel(cell)}`;
  if (!interactive) {
    return (
      <div
        aria-label={aria}
        title={cellTitle(cell, currency, automationText(automation))}
        className={className}
      >
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      aria-label={aria}
      aria-pressed={selected}
      title={cellTitle(cell, currency, automationText(automation))}
      className={className}
      onPointerDown={(event: PointerEvent) => {
        // Am Handy wählt erst das Antippen (click): ein Wisch zum Scrollen beginnt hier auch.
        if (event.pointerType === 'touch') return;
        event.preventDefault();
        onBegin!(day.date, modeOf(event));
      }}
      onClick={(event) => {
        if ((event.nativeEvent as PointerEvent['nativeEvent']).pointerType === 'touch') {
          onBegin!(day.date, modeOf(event), true);
        }
      }}
      onPointerEnter={() => onExtend?.(day.date)}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onBegin!(day.date, modeOf(event), true);
      }}
    >
      {body}
    </button>
  );
}

function modeOf(event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }): SelectionMode {
  if (event.ctrlKey || event.metaKey) return 'append';
  return event.shiftKey ? 'extend' : 'replace';
}
